import { useState, useRef, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Upload, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Loader2, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useImportUnits } from "@/hooks/useUnitSchedule";
import * as XLSX from "xlsx";
import { ColumnMappingList } from "./unit-import/ColumnMappingList";

const fmt = (n: number) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projectId: string;
  sovLines: any[];
  onComplete: () => void;
}

export type ColumnMapping = Record<number, string>; // colIndex -> sov_line_id | "unit_number" | "floor" | "skip"

export const UnitImportWizard = ({ open, onOpenChange, projectId, sovLines, onComplete }: Props) => {
  const [step, setStep] = useState(1);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [columnOrder, setColumnOrder] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const importUnits = useImportUnits();

  const [pasteText, setPasteText] = useState("");
  const [inputMode, setInputMode] = useState<"choose" | "file" | "paste">("choose");

  const reset = () => {
    setStep(1);
    setHeaders([]);
    setRows([]);
    setMapping({});
    setColumnOrder([]);
    setPasteText("");
    setInputMode("choose");
  };

  const processData = (hdrs: string[], dataRows: string[][]) => {
    setHeaders(hdrs);
    setRows(dataRows);

    const autoMap: ColumnMapping = {};
    const usedSovIds = new Set<string>();
    const order = hdrs.map((_, i) => i);

    // First pass: auto-detect unit_number (first col) and floor
    autoMap[0] = "unit_number";

    // Second pass: fuzzy match remaining headers to SOV lines (unique only)
    hdrs.forEach((h, i) => {
      if (i === 0) return;
      const hLower = h.toLowerCase().replace(/[^a-z0-9]/g, "");

      if (hLower.includes("floor") || hLower === "flr") {
        autoMap[i] = "floor";
        return;
      }

      // Only match if header has meaningful length (>2 chars after cleanup)
      if (hLower.length <= 2) {
        autoMap[i] = "skip";
        return;
      }

      let bestMatch: string | null = null;
      let bestScore = 0;

      for (const sl of sovLines) {
        if (usedSovIds.has(sl.id)) continue;
        const desc = (sl.description || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        if (!desc || desc.length <= 2) continue;

        // Check if header words appear in SOV description or vice versa
        const hWords = hLower.match(/[a-z]{3,}/g) || [];
        const dWords = desc.match(/[a-z]{3,}/g) || [];

        let score = 0;
        for (const hw of hWords) {
          for (const dw of dWords) {
            if (hw.includes(dw) || dw.includes(hw)) {
              score += Math.min(hw.length, dw.length);
            }
          }
        }

        if (score > bestScore && score >= 3) {
          bestScore = score;
          bestMatch = sl.id;
        }
      }

      if (bestMatch) {
        autoMap[i] = bestMatch;
        usedSovIds.add(bestMatch);
      } else {
        autoMap[i] = "skip";
      }
    });

    setMapping(autoMap);
    setColumnOrder(order);
    setStep(2);
  };

  const handlePaste = () => {
    const trimmed = pasteText.trim();
    if (!trimmed) {
      toast.error("Paste your spreadsheet data first");
      return;
    }
    try {
      const lines = trimmed.split("\n").map(line => line.split("\t"));
      if (lines.length < 2) {
        toast.error("Need at least a header row and one data row");
        return;
      }
      const hdrs = lines[0].map(String);
      const dataRows = lines.slice(1).filter(row => row.some(cell => cell.trim()));
      if (dataRows.length === 0) {
        toast.error("No data rows found");
        return;
      }
      processData(hdrs, dataRows.map(r => r.map(String)));
    } catch (err: any) {
      toast.error("Failed to parse pasted data: " + err.message);
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target?.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        if (data.length < 2) {
          toast.error("File must have headers and at least one data row");
          return;
        }
        const hdrs = data[0].map(String);
        const dataRows = data.slice(1).filter(row => row.some(cell => String(cell).trim()));
        processData(hdrs, dataRows.map(r => r.map(String)));
      } catch (err: any) {
        toast.error("Failed to parse file: " + err.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = useCallback((colIdx: number, value: string) => {
    setMapping(prev => ({ ...prev, [colIdx]: value }));
  }, []);

  const unitNumberColIdx = Object.entries(mapping).find(([_, v]) => v === "unit_number")?.[0];
  const floorColIdx = Object.entries(mapping).find(([_, v]) => v === "floor")?.[0];

  const validation = useMemo(() => {
    if (!rows.length || !unitNumberColIdx) return null;
    const unIdx = Number(unitNumberColIdx);
    const units = rows.map(r => r[unIdx]?.trim()).filter(Boolean);
    const uniqueUnits = new Set(units);
    const sovValidation: Array<{ sovLineId: string; description: string; unitTotal: number; sovQty: number; diff: number }> = [];
    const mappedSovIds = Object.entries(mapping)
      .filter(([_, v]) => v !== "unit_number" && v !== "floor" && v !== "skip")
      .map(([colIdx, sovId]) => ({ colIdx: Number(colIdx), sovId }));
    for (const { colIdx, sovId } of mappedSovIds) {
      const sl = sovLines.find((s: any) => s.id === sovId);
      if (!sl) continue;
      const unitTotal = rows.reduce((s, r) => s + (parseFloat(r[colIdx]) || 0), 0);
      sovValidation.push({ sovLineId: sovId, description: sl.description, unitTotal, sovQty: Number(sl.quantity), diff: unitTotal - Number(sl.quantity) });
    }
    return { unitCount: uniqueUnits.size, sovValidation, mismatches: sovValidation.filter(v => Math.abs(v.diff) > 0.01) };
  }, [rows, mapping, sovLines, unitNumberColIdx]);

  const handleImport = async () => {
    if (!unitNumberColIdx || busy) return;
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const unIdx = Number(unitNumberColIdx);
      const flIdx = floorColIdx ? Number(floorColIdx) : null;
      const unitNumbers = new Set<string>();
      const units: Array<{ unit_number: string; floor_number?: string }> = [];
      const scopeItems: Array<{ unit_number: string; sov_line_id: string; quantity: number; has_scope: boolean }> = [];
      const mappedCols = Object.entries(mapping)
        .filter(([_, v]) => v !== "unit_number" && v !== "floor" && v !== "skip")
        .map(([ci, sovId]) => ({ colIdx: Number(ci), sovId }));
      for (const row of rows) {
        const unitNum = row[unIdx]?.trim();
        if (!unitNum || unitNumbers.has(unitNum)) continue;
        unitNumbers.add(unitNum);
        units.push({ unit_number: unitNum, floor_number: flIdx !== null ? row[flIdx]?.trim() || undefined : undefined });
        for (const { colIdx, sovId } of mappedCols) {
          const qty = parseFloat(row[colIdx]) || 0;
          scopeItems.push({ unit_number: unitNum, sov_line_id: sovId, quantity: qty, has_scope: qty > 0 });
        }
      }
      await importUnits.mutateAsync({ projectId, companyId, units, scopeItems, clearExisting: true });
      await supabase.from("projects").update({ has_unit_schedule: true } as any).eq("id", projectId);
      toast.success(`${units.length} units imported with ${mappedCols.length} SOV lines mapped`);
      reset();
      onOpenChange(false);
      onComplete();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Unit Schedule — Step {step} of 3</DialogTitle>
          <DialogDescription>
            {step === 1 && "Upload your Excel file or paste spreadsheet data with unit numbers and scope quantities"}
            {step === 2 && "Map your columns to SOV lines. Drag rows to reorder."}
            {step === 3 && "Review validation before importing"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 1: Choose input method */}
          {step === 1 && inputMode === "choose" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setInputMode("paste")}>
                  <ClipboardPaste className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">Paste from Spreadsheet</p>
                  <p className="text-xs text-muted-foreground mt-1">Copy cells from Excel or Google Sheets and paste here</p>
                </div>
                <div className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setInputMode("file")}>
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">Upload File</p>
                  <p className="text-xs text-muted-foreground mt-1">.xlsx, .xls, or .csv</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Your data should have:</p>
                <p>• Column 1: Unit numbers</p>
                <p>• Remaining columns: scope quantities (blank or 0 = scope not in that unit)</p>
              </div>
            </div>
          )}

          {step === 1 && inputMode === "file" && (
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors" onClick={() => fileRef.current?.click()}>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to select a file</p>
                <p className="text-xs text-muted-foreground mt-1">Accepts: .xlsx, .xls, .csv</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setInputMode("choose")}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
            </div>
          )}

          {step === 1 && inputMode === "paste" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Select your data in Excel or Google Sheets (including headers), copy it, then paste below:</p>
              <Textarea className="font-mono text-xs min-h-[200px]" placeholder={"Unit\tFlooring\tPaint\tTrim\n101\t1\t1\t1\n102\t1\t0\t1"} value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => { setPasteText(""); setInputMode("choose"); }}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                <Button onClick={handlePaste} disabled={!pasteText.trim()}>Parse Data <ArrowRight className="ml-1 h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* Step 2: Column Mapping with DnD */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm font-medium">Map your columns to SOV lines:</p>
              <ColumnMappingList
                headers={headers}
                mapping={mapping}
                columnOrder={columnOrder}
                sovLines={sovLines}
                onMappingChange={handleMappingChange}
                onOrderChange={setColumnOrder}
              />
            </div>
          )}

          {/* Step 3: Validation */}
          {step === 3 && validation && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{validation.unitCount} units found</Badge>
                {validation.mismatches.length > 0 ? (
                  <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> {validation.mismatches.length} mismatches</Badge>
                ) : (
                  <Badge className="gap-1 bg-primary"><CheckCircle2 className="h-3 w-3" /> All lines match</Badge>
                )}
              </div>
              <p className="text-sm font-medium">SOV Validation:</p>
              <div className="space-y-2">
                {validation.sovValidation.map((v) => {
                  const matches = Math.abs(v.diff) < 0.01;
                  return (
                    <div key={v.sovLineId} className="flex items-center justify-between text-sm">
                      <span className="truncate max-w-[200px]">{v.description}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-muted-foreground">Unit total: {fmt(v.unitTotal)}</span>
                        <span className="text-muted-foreground">SOV: {fmt(v.sovQty)}</span>
                        <span className={cn("font-mono", matches ? "text-primary" : "text-destructive")}>
                          {matches ? "✅ Matches" : `⚠️ Off by ${fmt(Math.abs(v.diff))}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {validation.mismatches.length > 0 && (
                <p className="text-xs text-muted-foreground">⚠️ Mismatches can be corrected after import.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer — always pinned at bottom */}
        <DialogFooter className="flex justify-between pt-4 border-t">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
            {step === 2 && (
              <Button onClick={() => setStep(3)} disabled={!Object.values(mapping).includes("unit_number")}>
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handleImport} disabled={busy}>
                {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Importing…</> : `Import ${validation?.unitCount ?? 0} Units →`}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
